import { computed, defineComponent, ref } from 'vue'
import type { AttachmentUploadProps } from './types'
import { FORM_INPUT_FIELD_PROPS } from '../form/input/types'
import {
  EAttachmentFileType,
  isSavedAttachment,
  type IAttachment,
  type ISavedDocument
} from 'findef'
import { FindexClient } from '@/integrations/findex'
import type { FindexFile } from '@/types/file'
import { useForm } from '../form/formv2/hooks/useForm'
import { Form } from '../form'
import { Grid } from '../grid'
import Icon from '../icon'
import Button from '../button'
import ImageIcon from 'assets/img/svg/icons/image.svg?url'
import TrashIcon from 'assets/img/svg/icons/trash-outline.svg?url'
import { reactiveComputed } from '@vueuse/core'
import { defineVariants } from '@/utils/style/variant'
import cubeAnimation from 'assets/animations/cube.json'
import lottie from 'lottie-web'
import { unwrapElementRef } from '@/utils/dom/unwrap'
import { useEffect } from '@/utils/reactive/useEffect'
import Tooltip from '../tooltip'
import { isBoxedRef } from '@/types/boxedRef'
import { useFindexSocket } from '~/composables/useFindexSocket'

const LoadingContent = defineComponent<{ progress: number }>({
  setup(props) {
    const elRef = ref()

    const createCanvas = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        alert(123)
        throw new Error('no ctx')
      }

      canvas.width = 128
      canvas.height = 128
      canvas.style.height = '80%'
      canvas.style.objectFit = 'cover'
      canvas.style.position = 'absolute'
      canvas.style.left = '0px'
      canvas.style.right = '0px'
      canvas.style.top = '0px'
      canvas.style.bottom = '0px'
      canvas.style.margin = 'auto'

      return { canvas, ctx }
    }

    const setupLottie = (el: HTMLElement) => {
      const { canvas, ctx } = createCanvas()

      el.innerHTML = ''
      el.append(canvas)
      lottie.loadAnimation<'canvas'>({
        renderer: 'canvas',
        loop: true,
        autoplay: true,
        animationData: cubeAnimation,
        rendererSettings: {
          context: ctx,
          progressiveLoad: true
        },
        container: undefined as any
      })
    }

    useEffect(() => {
      const el = unwrapElementRef(elRef)
      if (!el) return
      setupLottie(el)
    }, [elRef])

    return () => {
      return (
        <div class="w-full h-full relative">
          <div class="bg-primary-950 flex justify-center items-center relative overflow-hidden w-full h-full">
            <div ref={elRef} />
          </div>

          <div
            class="text-white"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: 'bold',
              fontSize: '1rem',
              zIndex: 10,
              backdropFilter: 'blur(1px)'
            }}
          >{`${props.progress.toFixed(1)}%`}</div>
        </div>
      )
    }
  }
})

LoadingContent.props = ['progress']

const useVariants = defineVariants({
  slots: {
    base: {
      props: {
        columns: 'fit-content(8ch)'
      }
    }
  },
  variants: {
    size: {
      s: {
        base: {
          props: {
            columns: 'fit-content(8ch)'
          }
        }
      }
    }
  }
})

const AttachmentUpload = defineComponent<AttachmentUploadProps>({
  name: 'AttachmentUpload',
  setup(props) {
    const classes = useVariants({ size: props.size });

    const socket = useFindexSocket();
    const attachmentLoading = ref<boolean>(false);

    const placeholderContent = computed(() => {
      const placeholder = unref(props.placeholderComponent);
      if (isBoxedRef(placeholder)) return unref(placeholder.ref);
      if (typeof placeholder === 'function') return <placeholder style={{ width: '100%', height: '100%' }} />;
      return placeholder;
    });

    watchEffect(() => {
      console.log('placeholder', placeholderContent.value, props.placeholderComponent)
    })

    const state = reactiveComputed<{
      attachment: ISavedDocument<IAttachment> | undefined
      filename: string | undefined
    }>(() => {
      return {
        attachment: (typeof props.value === 'object'
          ? props.value
          : undefined) as ISavedDocument<IAttachment>,
        filename: undefined
      }
    })

    const accept = computed(() => {
      if (props.filetype === EAttachmentFileType.IMAGE) return 'image/*'
      return '*'
    })

    const form = useForm<{ file: FindexFile }>({
      schema: computed(() => ({
        file: {
          $type: 'file',
          $componentType: 'file-upload',
          $value: isSavedAttachment(props.value) ? props.value : undefined,
          $locked: !props.canUpload,
          $lockedReason: props.disabledReason,
          $injectedProps: {
            accept: accept.value,
            size: 's'
          }
        }
      })),
      onStateChange: async (s) => {
        attachmentLoading.value = true;
        console.log('uploading...')
        state.filename = s.file.item.name;
        const resp = await socket.transfer({
          name: s.file.item.name,
          app: 'fileUpload',
          data: s.file.item,
          startParams: {
            mime: s.file.item.type || 'unknown'
          }
        });
        if (!resp || !resp.ok) {
          console.error(`Upload failed`, (resp as any)?.error || resp);
          attachmentLoading.value = false;
          return
        }

        const attach = resp.attachment as ISavedDocument<IAttachment> | undefined;
        if (!attach) {
          console.error(`Upload failed: missing attachment in response`, resp);
          attachmentLoading.value = false;
          return
        }
        console.log('done uploading')
        console.log({ attach })
        state.attachment = attach

        const fieldRef = props.fieldRef
        const reportChange = props.reportChange

        if (fieldRef && reportChange) {
          reportChange(fieldRef, attach)
        }

        if (props.dataRef) {
          props.dataRef.setValue(attach);
        }

        if (props.onFileUploaded) {
          if (Array.isArray(props.onFileUploaded)) {
            props.onFileUploaded.flat().forEach(fn => fn());
          } else {
            props.onFileUploaded();
          }
        }

        attachmentLoading.value = false;
      }
    })

    // const form = computed(() =>
    //
    // )

    const handleRemove = async () => {
      const fieldRef = props.fieldRef
      const reportChange = props.reportChange
      state.attachment = undefined
      if (fieldRef && reportChange) {
        reportChange(fieldRef, undefined, true)
      }

      if (props.dataRef) {
        props.dataRef.setValue(undefined, true);
        props.dataRef.setTouched(true);
        //props.dataRef.markChanged();
      }

      if (props.onRemove) {
        if (Array.isArray(props.onRemove)) {
          props.onRemove.forEach(async (it: any) => {
            await it();
          })
        } else {
          console.log(props.onRemove);
          await props.onRemove()
        }
      }
    }

    const preview = computed(() => {
      if (attachmentLoading.value) {
        return (
          <div class="bg-gray-200 relative w-full flex justify-center items-center aspect-square">
            <LoadingContent progress={0} />
          </div>
        )
      }

      switch (props.filetype) {
        case EAttachmentFileType.IMAGE: {
          const src = state.attachment
            ? FindexClient.urlForAttachment(state.attachment._id as string)
            : (typeof props.value === 'string' ? FindexClient.urlForAttachment(props.value) : undefined)//undefined
          return (
            <div
              class={[
                'text-gray-300 w-full aspect-square flex justify-center items-center',
                !src && placeholderContent.value
                  ? 'bg-transparent overflow-hidden'
                  : 'bg-gray-200'
              ]}
            >
              {src ? (
                <img
                  style={{
                    display: 'block',
                    width: '80%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover'
                  }}
                  src={src}
                />
              ) : placeholderContent.value ? (
                placeholderContent.value
              ) : (
                <span
                  style={{
                    width: '50%',
                    aspectRatio: '1 / 1'
                  }}
                >
                  <Icon src={ImageIcon} size={'100%'} fill="currentColor" />
                </span>
              )}
            </div>
          )
        }
        default: {
          return <div></div>
        }
      }
    })

    return () => {
      return (
        <Grid
          rows={'max-content max-content'}
          columns={classes.value.base.props.columns}
          gap="1rem"
          class={classes.value.base.class}
        >
          <Grid>{preview.value}</Grid>
          <Grid columns="max-content max-content" gap="0.5rem">
            <Form handler={form as any} />
            <Tooltip disabled={props.disabledReason === undefined} text={props.disabledReason}>
              <Button
                onClick={handleRemove}
                disabled={props.canDelete === false}
                tooltip="DELETE"
                variant="errorInfo"
                size="s"
                transparent
                outlined
                onlyIcon
                icon={{
                  src: TrashIcon,
                  size: 14,
                  fill: 'currentColor'
                }}
              />
            </Tooltip>
          </Grid>
        </Grid>
      )
    }
  }
})

AttachmentUpload.props = [
  ...FORM_INPUT_FIELD_PROPS,
  'filetype',
  'disabled',
  'canDelete',
  'canUpload',
  'disabledReason',
  'label',
  'placeholderComponent',
  'size',
  'fileUploadOptions',
  'onRemove',
  'directoryId',
  'onFileUploaded'
]

export default AttachmentUpload
